import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import axios from 'axios';
import { Button } from 'ui-components/components/button';
import { ModalService } from 'ui-components/services/modal-service';
import { testId } from 'ui-components/services/testid-service';

import { CostReviewModalContainer } from '../../containers/cost-review-modal';
import { usePusherEvent } from '../../hooks/use-pusher-event';
import { SERVER_BULK_ACTION_COST_REVIEW } from '../../services/pusher-service/pusher-contract-messages';
import { useFulfillmentPlannerContext } from '../context';
import style from './style.module.less';
import { ErrorModal } from '../../components/modals/error-modal';

const messages = defineMessages({
  genericCostReviewErrorMessageTitle: {
    defaultMessage: 'Cost Review Error',
    id: 'NIG11z',
    description: 'Generic Cost Review Error title for read only cost review',
  },
  genericCostReviewErrorMessage: {
    defaultMessage: 'Unable to process cost review: {errorMessage}',
    id: 'E1MKA5',
    description: 'Generic Cost Review Error message for read only cost review',
  },
});

/**
 * @deprecated
 * @param {object} props
 * @param {string} [props.className]
 * @param {string[]} props.fulfillmentPlanIds
 * @param {boolean} [props.linkDisabled] - Consider using the `getShouldDisableCostReview` helper function to determine this value
 * @param {string} [props.buttonClassName]
 * @returns {React.JSX.Element}
 */
const CostReviewLink = ({ className = null, fulfillmentPlanIds, linkDisabled = false, buttonClassName }) => {
  const intl = useIntl();
  const [jobGuid, setJobGuid] = useState();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [readOnlyCostReviewModal, setReadOnlyCostReviewModal] = useState(false);
  const { rateErrors, totalCost } = useFulfillmentPlannerContext();
  const disabled = linkDisabled || !!rateErrors?.length || totalCost?.value === 0 || modalIsOpen;

  const onClick = useCallback(async () => {
    if (modalIsOpen) {
      return;
    }
    setModalIsOpen(true);
    const { data } = await axios.post('/bulk/costreview', {
      fulfillmentPlanId: fulfillmentPlanIds,
    });
    const [jobGuidValue] = data.jobGuids || [];
    setJobGuid(jobGuidValue);
    setReadOnlyCostReviewModal(true);
  }, [fulfillmentPlanIds, modalIsOpen]);

  const openCostReviewModal = useCallback(
    (pusherMessage) => {
      const costReviewData = pusherMessage?.data || {};
      const { errorMessage } = costReviewData;
      if (errorMessage) {
        ModalService.launchModal({
          component: ErrorModal,
          props: {
            title: intl.formatMessage(messages.genericCostReviewErrorMessageTitle),
            message: intl.formatMessage(messages.genericCostReviewErrorMessage, { errorMessage }),
          },
        });
        setModalIsOpen(false);
        return;
      }
      if (readOnlyCostReviewModal) {
        ModalService.launchModal({
          component: CostReviewModalContainer,
          onClose: () => {
            setReadOnlyCostReviewModal(false);
            setModalIsOpen(false);
          },
          props: {
            jobGuid,
            costReviewData,
            readOnlyCostReviewModal,
            fulfillmentPlanIds,
          },
        });
      }
    },
    [readOnlyCostReviewModal, intl, jobGuid, fulfillmentPlanIds],
  );

  usePusherEvent(SERVER_BULK_ACTION_COST_REVIEW, openCostReviewModal);

  return (
    <div className={classnames(style['cost-review-link'], className)} {...testId('cost-review-link')}>
      <Button className={buttonClassName} disabled={disabled} onClick={onClick} theme='link'>
        <FormattedMessage defaultMessage='Cost Review' description='Readonly cost review modal link text' id='usLXrL' />
      </Button>
    </div>
  );
};

CostReviewLink.propTypes = {
  className: PropTypes.string,
  fulfillmentPlanIds: PropTypes.array.isRequired,
  linkDisabled: PropTypes.bool,
  buttonClassName: PropTypes.string,
};

export default CostReviewLink;
